const VALIDATION_STALE_FIELD_NAMES = new Set([
    "matched_flight_ids",
    "matched_flights",
    "allocation_type",
    "allocation_rule",
    "product_code",
    "cost_type",
    "quantity",
    "unit_price",
    "total_price",
]);

const LEGACY_FLIGHT_ADDITIONAL_INFO_FIELDS = new Set([
    "flight_date",
    "flight_number",
    "aircraft_registration",
    "departure_airport",
    "arrival_airport",
    "leg_number",
]);

const DIRECT_ALLOCATION_TYPE = "DIRECT";
const RULE_BASED_ALLOCATION_TYPE = "RULE_BASED";
const VALID_ALLOCATION_TYPES = new Set([DIRECT_ALLOCATION_TYPE, RULE_BASED_ALLOCATION_TYPE]);
// 按加油量分摊规则值，与 constants ALLOCATION_RULE.BY_FUEL_QUANTITY 保持一致（doc 70 §2.1）
const FUEL_QUANTITY_ALLOCATION_RULE = "BY_FUEL_QUANTITY";
export const AMOUNT_DELTA_TYPE = Object.freeze({
    UNIT_PRICE_DISPLAY_ROUNDING: "UNIT_PRICE_DISPLAY_ROUNDING",
    FORMULA_BASED_PRICING: "FORMULA_BASED_PRICING",
    MANUAL_REVIEW_ACCEPTED: "MANUAL_REVIEW_ACCEPTED",
});

const AMOUNT_DELTA_POLICY = Object.freeze({
    [AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING]: "unit-price-display-rounding-v1",
    [AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING]: "formula-based-pricing-v1",
    [AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED]: "manual-review-accepted-v1",
});

// UI 本地问题类型，不属于后端 amount_delta delta_type 枚举。
export const AMOUNT_ISSUE_TYPE = Object.freeze({
    UNCLASSIFIED_AMOUNT_MISMATCH: "UNCLASSIFIED_AMOUNT_MISMATCH",
});

export const AMOUNT_DELTA_ACTION = Object.freeze({
    ADJUST_UNIT_PRICE: "ADJUST_UNIT_PRICE",
    ACCEPT_DECLARED_AMOUNT: "ACCEPT_DECLARED_AMOUNT",
});

const AMOUNT_DELTA_DECISION_FIELD = "amount_delta_decision";
const DELTA_STATUS_ACCEPTED = "ACCEPTED";
const AMOUNT_TOLERANCE = 0.01;
const ROUNDING_MAX_DELTA = 0.02;
const ROUNDING_MAX_RATIO = 0.005;
const UNIT_PRICE_PRECISION = 12;

export const isEmptyValue = (value) => value === undefined || value === null || (typeof value === "string" && value.trim() === "");

export const normalizeProductCode = (value) => String(value ?? "").trim().toLowerCase();

export const resolveLineItemProductCode = (item = {}) => {
    if (!isEmptyValue(item.product_code)) {
        return item.product_code;
    }
    if (!isEmptyValue(item.additional_info?.product_code)) {
        return item.additional_info.product_code;
    }
    return null;
};

export const lineItemFieldPath = (invoiceIndex, sectionIndex, itemIndex) => `invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${itemIndex}]`;

const toFiniteNumber = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

const roundToPrecision = (value, precision) => Number(value.toFixed(precision));

const getRoundingMaxDelta = (totalPrice) => Math.max(ROUNDING_MAX_DELTA, Math.abs(totalPrice) * ROUNDING_MAX_RATIO);

const formatSignedAmount = (value, precision = 6) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return String(value);
    const sign = numberValue > 0 ? "+" : "";
    return `${sign}${numberValue.toFixed(precision)}`;
};

const buildIssueBase = (item, invoiceIndex, sectionIndex, itemIndex) => ({
    fieldPath: lineItemFieldPath(invoiceIndex, sectionIndex, itemIndex),
    invoiceIndex,
    sectionIndex,
    itemIndex,
    itemId: item.item_id,
    productCode: item.product_code,
    description: item.description,
    currency: item.currency,
    existingRemarks: item.remarks,
    staleDecision: Boolean(item[AMOUNT_DELTA_DECISION_FIELD]),
});

const buildRoundingIssue = (item, invoiceIndex, sectionIndex, itemIndex, displayedUnitPrice, impliedUnitPrice) => {
    const quantity = toFiniteNumber(item.quantity);
    const totalPrice = toFiniteNumber(item.total_price);
    if (quantity === null || totalPrice === null || Math.min(quantity, displayedUnitPrice, impliedUnitPrice, totalPrice) <= 0) return null;
    const calculatedAmount = quantity * displayedUnitPrice;
    const amountDelta = totalPrice - calculatedAmount;
    if (Math.abs(amountDelta) <= AMOUNT_TOLERANCE || Math.abs(amountDelta) > getRoundingMaxDelta(totalPrice)) return null;
    if (Math.abs(quantity * impliedUnitPrice - totalPrice) > AMOUNT_TOLERANCE) return null;
    return {
        ...buildIssueBase(item, invoiceIndex, sectionIndex, itemIndex),
        deltaType: AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING,
        resolutionAction: AMOUNT_DELTA_ACTION.ADJUST_UNIT_PRICE,
        quantity,
        displayedUnitPrice,
        impliedUnitPrice,
        calculatedAmount,
        declaredAmount: totalPrice,
        amountDelta,
        suggestedReason: "供应商单价展示精度导致数量×票面单价与票面金额存在小额差异。",
    };
};

const getCurrentAuthoritativeClassification = (
    invoiceIndex,
    sectionIndex,
    itemIndex,
    classifications,
) => {
    return classifications?.[`${invoiceIndex}:${sectionIndex}:${itemIndex}`] || null;
};

const getLineAmountIssue = (item, invoiceIndex, sectionIndex, itemIndex, options) => {
    if (item[AMOUNT_DELTA_DECISION_FIELD]?.delta_status === DELTA_STATUS_ACCEPTED) return null;

    const quantity = toFiniteNumber(item.quantity);
    const unitPrice = toFiniteNumber(item.unit_price);
    const totalPrice = toFiniteNumber(item.total_price);
    if (quantity === null || unitPrice === null || totalPrice === null || quantity <= 0 || unitPrice === 0) return null;

    const staleDecision = item[AMOUNT_DELTA_DECISION_FIELD];
    const staleDetail = staleDecision?.delta_detail || {};

    const calculatedAmount = quantity * unitPrice;
    const amountDelta = totalPrice - calculatedAmount;
    if (Math.abs(amountDelta) <= AMOUNT_TOLERANCE) return null;

    if (Math.min(quantity, unitPrice, totalPrice) > 0 && Math.abs(amountDelta) <= getRoundingMaxDelta(totalPrice)) {
        const impliedUnitPrice = roundToPrecision(totalPrice / quantity, UNIT_PRICE_PRECISION);
        const roundingIssue = buildRoundingIssue(item, invoiceIndex, sectionIndex, itemIndex, unitPrice, impliedUnitPrice);
        if (roundingIssue) return roundingIssue;
    }

    const authoritativeClassification = getCurrentAuthoritativeClassification(
        invoiceIndex,
        sectionIndex,
        itemIndex,
        options.classifications,
    );
    if (authoritativeClassification?.delta_type === AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING) return {
        ...buildIssueBase(item, invoiceIndex, sectionIndex, itemIndex),
        deltaType: AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING,
        resolutionAction: AMOUNT_DELTA_ACTION.ACCEPT_DECLARED_AMOUNT,
        quantity,
        unitPrice,
        calculatedAmount,
        declaredAmount: totalPrice,
        amountDelta,
        formulaDescription: item.formula_description || staleDetail.formula_description || "",
        pricingBasis: item.pricing_basis || staleDetail.pricing_basis || "",
        reason: staleDetail.reason || "",
        authoritativeContractItemId: authoritativeClassification.contract_item_id,
    };

    return {
        ...buildIssueBase(item, invoiceIndex, sectionIndex, itemIndex),
        deltaType: AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED,
        resolutionAction: AMOUNT_DELTA_ACTION.ACCEPT_DECLARED_AMOUNT,
        quantity,
        unitPrice,
        calculatedAmount,
        declaredAmount: totalPrice,
        amountDelta,
        reason: staleDetail.reason || "",
        authorityReason: authoritativeClassification?.reason || "",
    };
};

export const findAmountIssues = (payload = {}, options = {}) => {
    const issues = [];
    const invoices = Array.isArray(payload?.invoices) ? payload.invoices : [];
    invoices.forEach((invoice, invoiceIndex) => {
        const sections = Array.isArray(invoice?.sections) ? invoice.sections : [];
        sections.forEach((section, sectionIndex) => {
            const lineItems = Array.isArray(section?.line_items) ? section.line_items : [];
            lineItems.forEach((item, itemIndex) => {
                const issue = getLineAmountIssue(item, invoiceIndex, sectionIndex, itemIndex, options);
                if (issue) issues.push(issue);
            });
        });
    });
    return issues;
};

export const buildAmountDeltaDecision = (issue, resolution = {}) => {
    if (issue.deltaType === AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING) {
        return {
            delta_type: issue.deltaType,
            amount_delta: roundToPrecision(issue.amountDelta, 6),
            delta_status: DELTA_STATUS_ACCEPTED,
            delta_policy: AMOUNT_DELTA_POLICY[AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING],
            delta_detail: {
                resolution_action: AMOUNT_DELTA_ACTION.ADJUST_UNIT_PRICE,
                displayed_unit_price: issue.displayedUnitPrice,
                implied_unit_price: issue.impliedUnitPrice,
                calculated_amount: roundToPrecision(issue.calculatedAmount, 12),
                reason: issue.suggestedReason,
            },
        };
    }

    if (issue.deltaType === AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED) {
        return {
            delta_type: issue.deltaType,
            amount_delta: roundToPrecision(issue.amountDelta, 6),
            delta_status: DELTA_STATUS_ACCEPTED,
            delta_policy: AMOUNT_DELTA_POLICY[AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED],
            delta_detail: {
                resolution_action: AMOUNT_DELTA_ACTION.ACCEPT_DECLARED_AMOUNT,
                calculated_amount: roundToPrecision(issue.calculatedAmount, 12),
                declared_amount: issue.declaredAmount,
                reason: String(resolution.reason || issue.reason || "").trim(),
            },
        };
    }

    if (issue.deltaType !== AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING) {
        throw new Error("当前金额问题没有可生成处理决定的权威分类");
    }

    return {
        delta_type: issue.deltaType,
        amount_delta: roundToPrecision(issue.amountDelta, 6),
        delta_status: DELTA_STATUS_ACCEPTED,
        delta_policy: AMOUNT_DELTA_POLICY[AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING],
        delta_detail: {
            resolution_action: AMOUNT_DELTA_ACTION.ACCEPT_DECLARED_AMOUNT,
            calculated_amount: roundToPrecision(issue.calculatedAmount, 12),
            declared_amount: issue.declaredAmount,
            formula_description: String(resolution.formulaDescription || issue.formulaDescription || "").trim(),
            pricing_basis: String(resolution.pricingBasis || issue.pricingBasis || "").trim(),
            reason: String(resolution.reason || issue.reason || "").trim(),
        },
    };
};

export const buildAmountIssueRemark = (issue, resolution = {}) => {
    if (issue.deltaType === AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING) {
        return `金额差异处理：因供应商单价展示精度，数量×票面单价与票面金额差异 ${formatSignedAmount(issue.amountDelta)}；`
            + `审核单价由 ${Number(issue.displayedUnitPrice).toFixed(6)} 调整为 ${Number(issue.impliedUnitPrice).toFixed(UNIT_PRICE_PRECISION)}，`
            + `保留票面金额 ${Number(issue.declaredAmount).toFixed(2)}。`;
    }
    if (issue.deltaType === AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED) {
        const reason = String(resolution.reason || issue.reason || "").trim();
        return `金额差异处理：人工审核接受票面金额 ${Number(issue.declaredAmount).toFixed(2)}；审核说明：${reason}。`;
    }
    if (issue.deltaType !== AMOUNT_DELTA_TYPE.FORMULA_BASED_PRICING) return "";
    const pricingBasis = String(resolution.pricingBasis || issue.pricingBasis || "").trim();
    const reason = String(resolution.reason || issue.reason || "").trim();
    return `金额差异处理：复杂计价按票面金额 ${Number(issue.declaredAmount).toFixed(2)} 入账；计价依据：${pricingBasis}；审核说明：${reason}。`;
};

export const appendAmountIssueRemark = (existingRemarks, amountIssueRemark) => {
    const normalizedExisting = String(existingRemarks || "").trim();
    if (!normalizedExisting) return amountIssueRemark;
    if (normalizedExisting.includes(amountIssueRemark)) return normalizedExisting;
    return `${normalizedExisting}\n${amountIssueRemark}`;
};

export const buildAmountIssueUpdates = (issue, resolution = {}) => {
    if (issue.deltaType === AMOUNT_DELTA_TYPE.MANUAL_REVIEW_ACCEPTED) {
        const reason = String(resolution?.reason || issue.reason || "").trim();
        if (!reason) return [];
    }
    const updates = [];
    if (issue.deltaType === AMOUNT_DELTA_TYPE.UNIT_PRICE_DISPLAY_ROUNDING) {
        updates.push({ path: `${issue.fieldPath}.unit_price`, value: issue.impliedUnitPrice });
    }
    const remark = buildAmountIssueRemark(issue, resolution);
    updates.push(
        { path: `${issue.fieldPath}.${AMOUNT_DELTA_DECISION_FIELD}`, value: buildAmountDeltaDecision(issue, resolution) },
        { path: `${issue.fieldPath}.remarks`, value: appendAmountIssueRemark(issue.existingRemarks, remark) },
    );
    return updates;
};

export const flightSelectionFieldUpdates = (fieldPath, selectedFlights = []) => ({
    matchedFlightIdsPath: `${fieldPath}.matched_flight_ids`,
    matchedFlightsPath: `${fieldPath}.matched_flights`,
    matchedFlightIds: selectedFlights.map((flight) => flight.id),
    matchedFlights: selectedFlights,
});

export const hasPendingFlightSelectionChange = (appliedFlights = [], pendingFlights = []) => {
    const appliedIds = appliedFlights.map((flight) => String(flight?.id ?? ""));
    const pendingIds = pendingFlights.map((flight) => String(flight?.id ?? ""));
    if (appliedIds.length !== pendingIds.length) return true;
    return appliedIds.some((id, index) => id !== pendingIds[index]);
};

export const getLineItemFlightAllocationError = (item = {}, itemLabel = "该明细") => {
    const matchedFlightIds = Array.isArray(item.matched_flight_ids) ? item.matched_flight_ids.filter((id) => !isEmptyValue(id)) : [];
    if (matchedFlightIds.length === 0) {
        return `审核失败：${itemLabel}必须至少关联一个航班`;
    }

    const allocationType = item.allocation_type || DIRECT_ALLOCATION_TYPE;
    if (!VALID_ALLOCATION_TYPES.has(allocationType)) {
        return `审核失败：${itemLabel}的分摊类型 ${allocationType} 无效`;
    }

    if (allocationType === DIRECT_ALLOCATION_TYPE && matchedFlightIds.length > 1) {
        return `审核失败：${itemLabel}关联了多个航班，必须切换为规则分摊并选择分摊规则`;
    }

    if (allocationType === RULE_BASED_ALLOCATION_TYPE && isEmptyValue(item.allocation_rule)) {
        return `审核失败：${itemLabel}使用规则分摊但缺少分摊规则`;
    }

    // 按加油量分摊必须声明权重来源，否则无法聚合油量权重（doc 70 §2.1 / §5.1）
    if (item.allocation_rule === FUEL_QUANTITY_ALLOCATION_RULE) {
        const weightSource = item.weight_source;
        const declaredCodes = Array.isArray(weightSource?.product_codes)
            ? weightSource.product_codes.filter((code) => !isEmptyValue(code))
            : [];
        if (!weightSource || declaredCodes.length === 0) {
            return `审核失败：${itemLabel}采用按加油量分摊需要指定权重来源（weight_source.product_codes）`;
        }
    }

    return null;
};

export const dirtyFieldList = (dirtyFields) => (dirtyFields instanceof Set ? Array.from(dirtyFields) : Array.isArray(dirtyFields) ? dirtyFields : []);

export const hasDirtyPath = (dirtyFields, fieldPath) => dirtyFieldList(dirtyFields).some((path) => path === fieldPath || path.startsWith(`${fieldPath}.`));

export const isLineItemModified = (dirtyFields, fieldPath) => hasDirtyPath(dirtyFields, fieldPath);

export const isLineItemValidationStale = (dirtyFields, fieldPath) => dirtyFieldList(dirtyFields).some((path) => {
    if (path === fieldPath) return true;
    if (!path.startsWith(`${fieldPath}.`)) return false;
    const fieldName = path.slice(fieldPath.length + 1).split(/[.[\]]/).filter(Boolean)[0];
    return VALIDATION_STALE_FIELD_NAMES.has(fieldName);
});

const sanitizeLineItemAdditionalInfoForSave = (additionalInfo) => {
    if (!additionalInfo || typeof additionalInfo !== "object" || Array.isArray(additionalInfo)) {
        return null;
    }

    const sanitizedInfo = { ...additionalInfo };
    LEGACY_FLIGHT_ADDITIONAL_INFO_FIELDS.forEach((fieldName) => {
        delete sanitizedInfo[fieldName];
    });
    return Object.keys(sanitizedInfo).length > 0 ? sanitizedInfo : null;
};

export const resolveProductInfo = (productMap = {}, productCode) => {
    if (!productCode || !productMap || typeof productMap !== "object") {
        return { productInfo: null, matchedKey: null };
    }

    if (Object.prototype.hasOwnProperty.call(productMap, productCode)) {
        return { productInfo: productMap[productCode], matchedKey: productCode };
    }

    const normalizedCode = normalizeProductCode(productCode);
    const matchedKey = Object.keys(productMap).find((key) => normalizeProductCode(key) === normalizedCode);
    return matchedKey ? { productInfo: productMap[matchedKey], matchedKey } : { productInfo: null, matchedKey: null };
};

const resolveLiveLineItemCostType = ({ item = {}, invoiceMeta = {}, invoiceDataSource = {} } = {}) => {
    const productCode = resolveLineItemProductCode(item);
    const hasProductCode = !isEmptyValue(productCode);
    const { productInfo, matchedKey } = resolveProductInfo(invoiceMeta.product_map, productCode);
    if (productInfo && typeof productInfo === "object" && !isEmptyValue(productInfo.cost_type)) {
        return {
            costType: String(productInfo.cost_type).trim(),
            source: "product_map",
            sourceLabel: "产品映射费用类型",
            productCode: matchedKey || productCode,
        };
    }
    if (hasProductCode) {
        return {
            costType: null,
            source: "unmapped_product_code",
            sourceLabel: "产品代码未配置费用类型",
            productCode,
        };
    }

    if (!isEmptyValue(item.cost_type)) {
        return {
            costType: String(item.cost_type).trim(),
            source: "line_item",
            sourceLabel: "行项目费用类型",
        };
    }

    const fallbackCostType = invoiceDataSource.standard_cost_type ?? invoiceDataSource.standardCostType ?? invoiceMeta.standard_cost_type ?? invoiceMeta.standardCostType;
    if (!isEmptyValue(fallbackCostType)) {
        return {
            costType: String(fallbackCostType).trim(),
            source: "standard_cost_type",
            sourceLabel: "账单数据源标准费用类型",
        };
    }

    return {
        costType: null,
        source: null,
        sourceLabel: null,
    };
};

export const resolveLineItemCostType = ({ item = {}, invoiceMeta = {}, invoiceDataSource = {} } = {}) => {
    const liveResolution = resolveLiveLineItemCostType({ item, invoiceMeta, invoiceDataSource });

    // 已入库账单：committed_cost_type 是入库那一刻冻结的值（后端只读附加）。
    // 展示以冻结值为准，避免"当前产品档案变更 → 历史账单显示跟着变"的误导；
    // 同时保留按当前档案现算的结果，用于标记两者不一致（提示可能需要作废重发）。
    if (isEmptyValue(item.committed_cost_type)) {
        return liveResolution;
    }

    const committedCostType = String(item.committed_cost_type).trim();
    return {
        costType: committedCostType,
        source: "committed_line_item",
        sourceLabel: "入库冻结费用类型",
        productCode: liveResolution.productCode,
        currentCostType: liveResolution.costType,
        currentSourceLabel: liveResolution.sourceLabel,
        isStaleAgainstCurrent: !isEmptyValue(liveResolution.costType) && String(liveResolution.costType).trim() !== committedCostType,
    };
};

export const prepareReviewPayloadForSave = (reconciliationData = {}, dirtyFields) => {
    const payloadToSave = JSON.parse(JSON.stringify(reconciliationData || {}));

    if (payloadToSave.invoices) {
        for (let invoiceIndex = 0; invoiceIndex < payloadToSave.invoices.length; invoiceIndex += 1) {
            const invoice = payloadToSave.invoices[invoiceIndex];
            delete invoice.invoice_summary;
            delete invoice.header_variance_attribution;
            if (invoice.meta) delete invoice.meta.header_variance_attribution;
            for (let sectionIndex = 0; sectionIndex < (invoice.sections || []).length; sectionIndex += 1) {
                const section = invoice.sections[sectionIndex];
                for (let itemIndex = 0; itemIndex < (section.line_items || []).length; itemIndex += 1) {
                    const item = section.line_items[itemIndex];
                    const fieldPath = lineItemFieldPath(invoiceIndex, sectionIndex, itemIndex);
                    delete item.matched_flights;
                    const additionalInfo = sanitizeLineItemAdditionalInfoForSave(item.additional_info);
                    if (additionalInfo) {
                        item.additional_info = additionalInfo;
                    } else {
                        delete item.additional_info;
                    }
                    delete item.match_reasoning;
                    // 后端只读附加的入库冻结值，不允许回写进 user_modified_payload
                    delete item.committed_cost_type;
                    if (isLineItemValidationStale(dirtyFields, fieldPath)) {
                        delete item.anomalies;
                    }
                }
            }
        }
    }

    return payloadToSave;
};

const normalizeCostType = (value) => String(value ?? "").trim().toUpperCase();

const normalizeCurrency = (value) => String(value ?? "").trim().toUpperCase();

const toMatrixEntries = (productsMatrix) => Object.entries(productsMatrix || {}).filter(([, value]) => value && typeof value === "object");

const matrixEntryHasError = (entryData = {}) => entryData.match === false;

const RELATED_ALIAS_AMOUNT_TOLERANCE = 0.01;

// 矩阵条目币种：优先取 currency 字段，降级从 "product_code::CURRENCY" 复合键解析（兼容前端 fallback 构建的旧格式）
const resolveMatrixEntryCurrency = (key, data = {}) => data.currency ?? (String(key).includes("::") ? String(key).split("::").pop() : null);

// 识别"Agent 把金额记到同费用类型的其他产品编码下"的关联行：
// 同 cost_type + 同 currency、标准值≈0、且至少一个 Agent 提取到正金额
const findRelatedAliasEntries = (entries, currentKey, currentData) => {
    const currentCostType = normalizeCostType(currentData.cost_type);
    if (!currentCostType) return [];
    const currentCurrency = normalizeCurrency(resolveMatrixEntryCurrency(currentKey, currentData));
    return entries.filter(([key, data]) => {
        if (key === currentKey) return false;
        if (normalizeCostType(data.cost_type) !== currentCostType) return false;
        if (normalizeCurrency(resolveMatrixEntryCurrency(key, data)) !== currentCurrency) return false;
        const standard = Number(data.standard);
        if (!Number.isFinite(standard) || Math.abs(standard) > RELATED_ALIAS_AMOUNT_TOLERANCE) return false;
        return (data.agent_results || []).some((result) => !result?.missing && !result?.execution_failed && Number(result?.value) > RELATED_ALIAS_AMOUNT_TOLERANCE);
    });
};

export const resolveProductsMatrixScope = (productsMatrix, { productCode, costType } = {}) => {
    const entries = toMatrixEntries(productsMatrix);
    if (entries.length === 0) {
        return { scope: "empty", productsMatrix: null, reason: "empty" };
    }

    const normalizedProductCode = normalizeProductCode(productCode);
    if (normalizedProductCode) {
        const productMatches = entries.filter(([code, data]) => normalizeProductCode(code) === normalizedProductCode || normalizeProductCode(data.product_code) === normalizedProductCode);
        if (productMatches.length === 1) {
            const productMatch = productMatches[0];
            const scopedMatrix = { [productMatch[0]]: productMatch[1] };
            // 受控扩展：仅当前行判定失败时，纳入"Agent 实际记账编码"关联行，
            // 揭示"编码不一致但金额可能一致"的真实失败原因；当前行通过时保持原 scope 不变
            const relatedProductKeys = matrixEntryHasError(productMatch[1]) ? findRelatedAliasEntries(entries, productMatch[0], productMatch[1]).map(([key]) => key) : [];
            for (const key of relatedProductKeys) {
                scopedMatrix[key] = productsMatrix[key];
            }
            return {
                scope: "line",
                productsMatrix: scopedMatrix,
                reason: "product_code",
                relatedProductKeys,
            };
        }
        if (productMatches.length > 1) {
            return {
                scope: "flight",
                productsMatrix,
                reason: "ambiguous_product_code",
            };
        }
    }

    const normalizedCostType = normalizeCostType(costType);
    if (normalizedCostType) {
        const costTypeMatches = entries.filter(([, data]) => normalizeCostType(data.cost_type) === normalizedCostType);
        if (costTypeMatches.length === 1) {
            return {
                scope: "line",
                productsMatrix: { [costTypeMatches[0][0]]: costTypeMatches[0][1] },
                reason: "cost_type",
            };
        }
        if (costTypeMatches.length > 1) {
            return {
                scope: "flight",
                productsMatrix,
                reason: "ambiguous_cost_type",
            };
        }
    }

    return {
        scope: "flight",
        productsMatrix,
        reason: "unmatched_product",
    };
};

const hasFlightValidationError = (validationResult = {}) => Boolean(validationResult.has_error || validationResult.status === "HAS_ERROR" || validationResult.error_count > 0);

export const resolveLineItemValidationState = (validationResult, { productsMatrix, productCode, costType } = {}) => {
    if (!validationResult) {
        return {
            status: "NOT_VALIDATED",
            scope: "none",
            reason: "missing_validation_result",
            productsMatrix: null,
            hasOtherProductErrors: false,
        };
    }

    const effectiveProductsMatrix = productsMatrix || validationResult.products_matrix || null;
    const matrixScope = resolveProductsMatrixScope(effectiveProductsMatrix, { productCode, costType });
    const allEntries = toMatrixEntries(effectiveProductsMatrix);
    const flightHasError = hasFlightValidationError(validationResult);

    if (matrixScope.scope === "line") {
        const scopedEntries = toMatrixEntries(matrixScope.productsMatrix);
        const scopedKeys = new Set(scopedEntries.map(([code]) => code));
        const lineHasError = scopedEntries.some(([, data]) => matrixEntryHasError(data));
        const hasOtherProductErrors = allEntries.some(([code, data]) => !scopedKeys.has(code) && matrixEntryHasError(data));

        return {
            status: lineHasError ? "HAS_ERRORS" : "PASSED",
            scope: "line",
            reason: matrixScope.reason,
            productsMatrix: matrixScope.productsMatrix,
            relatedProductKeys: matrixScope.relatedProductKeys || [],
            hasOtherProductErrors,
            flightHasError,
        };
    }

    return {
        status: flightHasError ? "HAS_ERRORS" : "PASSED",
        scope: "flight",
        reason: matrixScope.reason,
        productsMatrix: matrixScope.productsMatrix || effectiveProductsMatrix,
        hasOtherProductErrors: allEntries.some(([, data]) => matrixEntryHasError(data)),
        flightHasError,
    };
};
